/**
 * LiveWorkoutModal - Real-time workout session screen
 *
 * Features:
 * - Pre-planned exercises displayed as checklist
 * - Set-by-set entry with timer
 * - Companion mode (coach works out alongside student)
 * - Progress tracking
 * - Auto-save to database on completion
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Play, Pause, ChevronDown, ChevronUp,
  User, Clock, Dumbbell, Timer, Plus, Trash2,
  Users, Flag, AlertCircle, Loader2, RotateCcw, MessageSquare
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useFitnessStore } from '../../stores/fitnessStore';
import { useCoachStore } from '../../stores/coachStore';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';

// Timer display component
const TimerDisplay = ({ seconds, isRunning, size = 'normal' }) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const sizeClasses = size === 'large' ? 'text-3xl' : 'text-sm';

  return (
    <span className={`font-mono ${sizeClasses} ${isRunning ? 'text-green-400' : 'text-gray-400'}`}>
      {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
    </span>
  );
};

// Set row component
const SetRow = ({
  set,
  setIndex,
  exerciseIndex,
  isCompanionMode,
  onUpdate,
  // Student timer
  onStartStudentTimer,
  onStopStudentTimer,
  onResetStudentTimer,
  isStudentTimerRunning,
  studentSetTimer,
  // Coach timer
  onStartCoachTimer,
  onStopCoachTimer,
  onResetCoachTimer,
  isCoachTimerRunning,
  coachSetTimer,
  // Avatars
  clientAvatar,
  coachAvatar,
  // History source for label
  historySource
}) => {
  const isCompleted = set.actual_reps !== null && set.actual_reps !== '';
  const isCoachCompleted = set.coach_reps !== null && set.coach_reps !== '';
  const hasStudentTimerData = set.started_at || set.duration_seconds;
  const hasCoachTimerData = set.coach_started_at || set.coach_duration_seconds;

  // Determine label based on history source
  const targetLabel = historySource === 'max' ? 'Max' : historySource === 'last' ? 'En Son' : 'Hedef';
  const canStartStudentTimer = set.actual_reps && set.actual_weight;
  const canStartCoachTimer = set.coach_reps && set.coach_weight;

  // Check if timer is running from DB (other user started it)
  const isStudentTimerRunningFromDB = set.started_at && !set.completed_at && !isStudentTimerRunning;
  const isCoachTimerRunningFromDB = set.coach_started_at && !set.coach_completed_at && !isCoachTimerRunning;

  // Calculate elapsed time from DB started_at
  const [studentElapsedFromDB, setStudentElapsedFromDB] = useState(0);
  const [coachElapsedFromDB, setCoachElapsedFromDB] = useState(0);

  // Update elapsed time every second for remote timers
  useEffect(() => {
    if (!isStudentTimerRunningFromDB) {
      setStudentElapsedFromDB(0);
      return;
    }
    const startTime = new Date(set.started_at).getTime();
    const updateElapsed = () => {
      setStudentElapsedFromDB(Math.floor((Date.now() - startTime) / 1000));
    };
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [isStudentTimerRunningFromDB, set.started_at]);

  useEffect(() => {
    if (!isCoachTimerRunningFromDB) {
      setCoachElapsedFromDB(0);
      return;
    }
    const startTime = new Date(set.coach_started_at).getTime();
    const updateElapsed = () => {
      setCoachElapsedFromDB(Math.floor((Date.now() - startTime) / 1000));
    };
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [isCoachTimerRunningFromDB, set.coach_started_at]);

  // Effective timer values (local or from DB)
  const effectiveStudentTimerRunning = isStudentTimerRunning || isStudentTimerRunningFromDB;
  const effectiveStudentTimer = isStudentTimerRunning ? studentSetTimer : studentElapsedFromDB;
  const effectiveCoachTimerRunning = isCoachTimerRunning || isCoachTimerRunningFromDB;
  const effectiveCoachTimer = isCoachTimerRunning ? coachSetTimer : coachElapsedFromDB;

  // Single row layout (no companion mode)
  if (!isCompanionMode) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="p-3 rounded-xl transition-all bg-gray-800/50"
      >
        <div className="flex items-center gap-3">
          {/* Set number + Timer button stacked vertically */}
          <div className="flex flex-col items-center gap-1">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm bg-gray-700 text-gray-400">
              {set.set_number}
            </div>
            <button
              onClick={() => effectiveStudentTimerRunning ? onStopStudentTimer() : onStartStudentTimer(exerciseIndex, setIndex)}
              disabled={!effectiveStudentTimerRunning && !canStartStudentTimer}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                effectiveStudentTimerRunning
                  ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                  : canStartStudentTimer
                    ? 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    : 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50'
              }`}
              title={!canStartStudentTimer && !effectiveStudentTimerRunning ? 'Önce tekrar ve kg girin' : ''}
            >
              {effectiveStudentTimerRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
          </div>

          {/* Timer display */}
          {effectiveStudentTimerRunning && (
            <TimerDisplay seconds={effectiveStudentTimer} isRunning={true} />
          )}

          {/* Target */}
          {(set.target_reps || set.target_weight) && (
            <div className={`text-xs px-2 py-1 rounded ${
              historySource ? 'text-orange-400 bg-orange-500/10' : 'text-gray-500 bg-gray-800'
            }`}>
              {targetLabel}: {set.target_reps && `${set.target_reps}×`}{set.target_weight && `${set.target_weight}kg`}
            </div>
          )}

          {/* Student inputs */}
          <div className="flex items-center gap-1.5 flex-1">
            <input
              type="number"
              inputMode="numeric"
              value={set.actual_reps || ''}
              onChange={(e) => onUpdate(exerciseIndex, setIndex, 'actual_reps', e.target.value)}
              placeholder="rep"
              className="w-14 px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-sm focus:outline-none focus:border-orange-500"
            />
            <span className="text-gray-600 text-xs">×</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.5"
              value={set.actual_weight || ''}
              onChange={(e) => onUpdate(exerciseIndex, setIndex, 'actual_weight', e.target.value)}
              placeholder="kg"
              className="w-16 px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-orange-400 text-center text-sm focus:outline-none focus:border-orange-500"
            />
          </div>

          {/* Duration & Reset */}
          {hasStudentTimerData && (
            <div className="flex items-center gap-2">
              {set.duration_seconds && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Timer className="w-3 h-3" />
                  {Math.floor(set.duration_seconds / 60)}:{String(set.duration_seconds % 60).padStart(2, '0')}
                </div>
              )}
              <button
                onClick={() => onResetStudentTimer(exerciseIndex, setIndex)}
                className="p-1 text-gray-500 hover:text-orange-400 hover:bg-orange-500/10 rounded transition-colors"
                title="Süreyi sıfırla"
              >
                <RotateCcw className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // Two-row layout for companion mode - each person has their own timer
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="p-3 rounded-xl transition-all space-y-2 bg-gray-800/50"
    >
      {/* Header: Set number and target */}
      <div className="flex items-center gap-2 pb-2 border-b border-gray-700/50">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-sm bg-gray-700 text-gray-400">
          {set.set_number}
        </div>
        {(set.target_reps || set.target_weight) && (
          <div className={`text-xs px-2 py-0.5 rounded ${
            historySource ? 'text-orange-400 bg-orange-500/10' : 'text-gray-500 bg-gray-800'
          }`}>
            {targetLabel}: {set.target_reps && `${set.target_reps}×`}{set.target_weight && `${set.target_weight}kg`}
          </div>
        )}
      </div>

      {/* Student row with timer */}
      <div className={`flex items-center gap-2 p-2 rounded-lg ${
        isCompleted ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-gray-700/30'
      }`}>
        {clientAvatar ? (
          <img src={clientAvatar} alt="Öğrenci" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-orange-500/30 flex items-center justify-center flex-shrink-0">
            <User className="w-3.5 h-3.5 text-orange-400" />
          </div>
        )}

        {/* Student timer button */}
        <button
          onClick={() => effectiveStudentTimerRunning ? onStopStudentTimer() : onStartStudentTimer(exerciseIndex, setIndex)}
          disabled={!effectiveStudentTimerRunning && !canStartStudentTimer}
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all flex-shrink-0 ${
            effectiveStudentTimerRunning
              ? 'bg-red-500/20 text-red-400 border border-red-500/50'
              : canStartStudentTimer
                ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                : 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50'
          }`}
        >
          {effectiveStudentTimerRunning ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
        </button>

        {/* Student timer display */}
        {effectiveStudentTimerRunning && (
          <span className="text-xs text-orange-400 font-mono w-10">
            {Math.floor(effectiveStudentTimer / 60)}:{String(effectiveStudentTimer % 60).padStart(2, '0')}
          </span>
        )}

        {/* Student inputs */}
        <div className="flex items-center gap-1 flex-1">
          <input
            type="number"
            inputMode="numeric"
            value={set.actual_reps || ''}
            onChange={(e) => onUpdate(exerciseIndex, setIndex, 'actual_reps', e.target.value)}
            placeholder="rep"
            className="w-14 px-1 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-sm focus:outline-none focus:border-orange-500"
          />
          <span className="text-gray-600 text-xs">×</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            value={set.actual_weight || ''}
            onChange={(e) => onUpdate(exerciseIndex, setIndex, 'actual_weight', e.target.value)}
            placeholder="kg"
            className="w-14 px-1 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-orange-400 text-center text-sm focus:outline-none focus:border-orange-500"
          />
        </div>

        {/* Student duration & reset */}
        {hasStudentTimerData && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {set.duration_seconds && (
              <span className="text-xs text-gray-500">
                {Math.floor(set.duration_seconds / 60)}:{String(set.duration_seconds % 60).padStart(2, '0')}
              </span>
            )}
            <button
              onClick={() => onResetStudentTimer(exerciseIndex, setIndex)}
              className="p-1 text-gray-500 hover:text-orange-400 rounded"
            >
              <RotateCcw className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Coach row with timer */}
      <div className={`flex items-center gap-2 p-2 rounded-lg ${
        isCoachCompleted ? 'bg-violet-500/10 border border-violet-500/30' : 'bg-gray-700/30'
      }`}>
        {coachAvatar ? (
          <img src={coachAvatar} alt="Koç" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-violet-500/30 flex items-center justify-center flex-shrink-0">
            <User className="w-3.5 h-3.5 text-violet-400" />
          </div>
        )}

        {/* Coach timer button */}
        <button
          onClick={() => effectiveCoachTimerRunning ? onStopCoachTimer() : onStartCoachTimer(exerciseIndex, setIndex)}
          disabled={!effectiveCoachTimerRunning && !canStartCoachTimer}
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all flex-shrink-0 ${
            effectiveCoachTimerRunning
              ? 'bg-red-500/20 text-red-400 border border-red-500/50'
              : canStartCoachTimer
                ? 'bg-violet-500/20 text-violet-400 hover:bg-violet-500/30'
                : 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50'
          }`}
        >
          {effectiveCoachTimerRunning ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
        </button>

        {/* Coach timer display */}
        {effectiveCoachTimerRunning && (
          <span className="text-xs text-violet-400 font-mono w-10">
            {Math.floor(effectiveCoachTimer / 60)}:{String(effectiveCoachTimer % 60).padStart(2, '0')}
          </span>
        )}

        {/* Coach inputs */}
        <div className="flex items-center gap-1 flex-1">
          <input
            type="number"
            inputMode="numeric"
            value={set.coach_reps || ''}
            onChange={(e) => onUpdate(exerciseIndex, setIndex, 'coach_reps', e.target.value)}
            placeholder="rep"
            className="w-14 px-1 py-1.5 bg-violet-900/30 border border-violet-600/50 rounded-lg text-violet-300 text-center text-sm focus:outline-none focus:border-violet-400"
          />
          <span className="text-violet-600 text-xs">×</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            value={set.coach_weight || ''}
            onChange={(e) => onUpdate(exerciseIndex, setIndex, 'coach_weight', e.target.value)}
            placeholder="kg"
            className="w-14 px-1 py-1.5 bg-violet-900/30 border border-violet-600/50 rounded-lg text-violet-400 text-center text-sm focus:outline-none focus:border-violet-400"
          />
        </div>

        {/* Coach duration & reset */}
        {hasCoachTimerData && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {set.coach_duration_seconds && (
              <span className="text-xs text-gray-500">
                {Math.floor(set.coach_duration_seconds / 60)}:{String(set.coach_duration_seconds % 60).padStart(2, '0')}
              </span>
            )}
            <button
              onClick={() => onResetCoachTimer(exerciseIndex, setIndex)}
              className="p-1 text-gray-500 hover:text-violet-400 rounded"
            >
              <RotateCcw className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Exercise card component
const ExerciseCard = ({
  exercise,
  exerciseIndex,
  isExpanded,
  onToggle,
  isCompanionMode,
  onUpdateSet,
  onAddSet,
  onRemoveSet,
  onUpdateNote,
  // Student timer
  onStartStudentTimer,
  onStopStudentTimer,
  onResetStudentTimer,
  studentActiveTimer,
  studentSetTimer,
  // Coach timer
  onStartCoachTimer,
  onStopCoachTimer,
  onResetCoachTimer,
  coachActiveTimer,
  coachSetTimer,
  // Exercise timers reset
  onResetExerciseTimers,
  // Avatars
  clientAvatar,
  coachAvatar
}) => {
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteValue, setNoteValue] = useState(exercise.notes || '');
  const completedSets = exercise.sets.filter(s => s.actual_reps !== null && s.actual_reps !== '').length;
  const totalSets = exercise.sets.length;
  const hasAnyTimerData = exercise.sets.some(s => s.started_at || s.duration_seconds);

  return (
    <motion.div
      layout
      className="rounded-2xl overflow-hidden transition-all bg-gray-800/50 border border-gray-700"
    >
      {/* Header */}
      <div
        onClick={() => onToggle(exerciseIndex)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-800/50 transition-colors cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle(exerciseIndex)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-500/20">
            <Dumbbell className="w-5 h-5 text-orange-400" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-white">{exercise.exercise_name}</h3>
            <p className="text-xs text-gray-500">
              {completedSets}/{totalSets} set tamamlandı
              {exercise.notes && ` • ${exercise.notes}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Note button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowNoteInput(!showNoteInput);
            }}
            className={`p-1.5 rounded-lg transition-colors ${
              exercise.notes
                ? 'text-blue-400 bg-blue-500/10 hover:bg-blue-500/20'
                : 'text-gray-500 hover:text-blue-400 hover:bg-blue-500/10'
            }`}
            title={exercise.notes || 'Not ekle'}
          >
            <MessageSquare className="w-4 h-4" />
          </button>
          {/* Reset exercise timers button */}
          {hasAnyTimerData && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onResetExerciseTimers(exerciseIndex);
              }}
              className="p-1.5 text-gray-500 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors"
              title="Tüm süreleri sıfırla"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          {/* Progress bar */}
          <div className="w-20 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${(completedSets / totalSets) * 100}%` }}
            />
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Note input */}
      <AnimatePresence>
        {showNoteInput && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-gray-700"
          >
            <div className="px-4 py-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={noteValue}
                  onChange={(e) => setNoteValue(e.target.value)}
                  placeholder="Egzersiz notu..."
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onUpdateNote(exerciseIndex, noteValue);
                      setShowNoteInput(false);
                    }
                  }}
                />
                <button
                  onClick={() => {
                    onUpdateNote(exerciseIndex, noteValue);
                    setShowNoteInput(false);
                  }}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                >
                  Kaydet
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sets (expanded) */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-2">
              {/* Column headers */}
              <div className="flex items-center gap-3 px-3 py-1 text-xs text-gray-500">
                <span className="w-8">Set</span>
                <span className="w-10">Timer</span>
                <span className="flex-1">Öğrenci</span>
                {isCompanionMode && <span className="pl-3 border-l border-violet-500/30">Koç</span>}
              </div>

              {/* Sets */}
              {exercise.sets.map((set, setIndex) => (
                <SetRow
                  key={set.set_number}
                  set={set}
                  setIndex={setIndex}
                  exerciseIndex={exerciseIndex}
                  isCompanionMode={isCompanionMode}
                  onUpdate={onUpdateSet}
                  // Student timer
                  onStartStudentTimer={onStartStudentTimer}
                  onStopStudentTimer={onStopStudentTimer}
                  onResetStudentTimer={onResetStudentTimer}
                  isStudentTimerRunning={studentActiveTimer?.exerciseIndex === exerciseIndex && studentActiveTimer?.setIndex === setIndex}
                  studentSetTimer={studentActiveTimer?.exerciseIndex === exerciseIndex && studentActiveTimer?.setIndex === setIndex ? studentSetTimer : 0}
                  // Coach timer
                  onStartCoachTimer={onStartCoachTimer}
                  onStopCoachTimer={onStopCoachTimer}
                  onResetCoachTimer={onResetCoachTimer}
                  isCoachTimerRunning={coachActiveTimer?.exerciseIndex === exerciseIndex && coachActiveTimer?.setIndex === setIndex}
                  coachSetTimer={coachActiveTimer?.exerciseIndex === exerciseIndex && coachActiveTimer?.setIndex === setIndex ? coachSetTimer : 0}
                  // Avatars
                  clientAvatar={clientAvatar}
                  coachAvatar={coachAvatar}
                  // History source for label
                  historySource={exercise.history_source}
                />
              ))}

              {/* Add/Remove set buttons */}
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => onAddSet(exerciseIndex)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 border-2 border-dashed border-gray-700 rounded-xl text-gray-400 hover:border-orange-500 hover:text-orange-400 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm">Set Ekle</span>
                </button>
                {exercise.sets.length > 1 && (
                  <button
                    onClick={() => onRemoveSet(exerciseIndex)}
                    className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main component
const LiveWorkoutModal = () => {
  const {
    isLiveWorkoutModalOpen,
    liveWorkoutData,
    closeLiveWorkoutModal,
    startLiveWorkout,
    updateLiveWorkoutExercises,
    completeLiveWorkout,
  } = useFitnessStore();

  const { viewingClientInfo, isActingAsCoach } = useCoachStore();

  // Local state
  const [exercises, setExercises] = useState([]);
  const [expandedExercise, setExpandedExercise] = useState(0);
  const [isCompanionMode, setIsCompanionMode] = useState(false);
  const [totalTimer, setTotalTimer] = useState(0);
  const [isWorkoutStarted, setIsWorkoutStarted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Separate timers for student and coach
  const [studentActiveTimer, setStudentActiveTimer] = useState(null); // { exerciseIndex, setIndex }
  const [studentSetTimer, setStudentSetTimer] = useState(0);
  const [coachActiveTimer, setCoachActiveTimer] = useState(null); // { exerciseIndex, setIndex }
  const [coachSetTimer, setCoachSetTimer] = useState(0);

  const totalTimerRef = useRef(null);
  const studentTimerRef = useRef(null);
  const coachTimerRef = useRef(null);

  // Body scroll lock
  useBodyScrollLock(isLiveWorkoutModalOpen);

  // Track previous workout ID to detect when a new workout is opened
  const prevWorkoutIdRef = useRef(null);
  const lastUpdateTimeRef = useRef(null);
  // Track last saved state to detect local (unsaved) changes
  const lastSavedExercisesRef = useRef(null);
  // Track if we've initialized companion mode for this workout (to prevent reset on remount)
  const companionModeInitializedRef = useRef(null);

  // Initialize exercises from liveWorkoutData - only on first load or workout change
  useEffect(() => {
    if (!liveWorkoutData?.planned_exercises) return;

    console.log('[LiveWorkoutModal] liveWorkoutData changed:', {
      id: liveWorkoutData.id,
      exerciseCount: liveWorkoutData.planned_exercises?.length,
      firstExerciseSets: liveWorkoutData.planned_exercises?.[0]?.sets,
      updated_at: liveWorkoutData.updated_at,
    });

    const isNewWorkout = prevWorkoutIdRef.current !== liveWorkoutData.id;
    const isFirstLoad = exercises.length === 0;

    console.log('[LiveWorkoutModal] Decision:', { isNewWorkout, isFirstLoad, prevId: prevWorkoutIdRef.current });

    // Only fully replace exercises when opening a new workout
    if (isNewWorkout || isFirstLoad) {
      console.log('[LiveWorkoutModal] FULL REPLACE - setting exercises from liveWorkoutData');
      prevWorkoutIdRef.current = liveWorkoutData.id;
      const newExercises = liveWorkoutData.planned_exercises;
      lastSavedExercisesRef.current = JSON.parse(JSON.stringify(newExercises)); // Deep copy

      // Defer state updates to avoid render-during-render warning
      queueMicrotask(() => {
        setExercises(newExercises);

        // Only set companion mode if we haven't initialized it for this workout yet
        if (companionModeInitializedRef.current !== liveWorkoutData.id) {
          companionModeInitializedRef.current = liveWorkoutData.id;
          setIsCompanionMode(liveWorkoutData.coach_participating || false);
        }

        // Only restore timer if workout is actually in_progress
        if (liveWorkoutData.status === 'in_progress' && liveWorkoutData.workout_started_at) {
          setIsWorkoutStarted(true);
          const startedAt = new Date(liveWorkoutData.workout_started_at);
          const elapsed = Math.floor((Date.now() - startedAt.getTime()) / 1000);
          setTotalTimer(elapsed);
        } else {
          setIsWorkoutStarted(false);
          setTotalTimer(0);
        }
      });
    } else {
      // Smart merge for realtime updates - preserve local unsaved changes
      const incomingUpdateTime = liveWorkoutData.updated_at;
      console.log('[LiveWorkoutModal] SMART MERGE check:', {
        incomingUpdateTime,
        lastUpdateTime: lastUpdateTimeRef.current,
        shouldUpdate: incomingUpdateTime !== lastUpdateTimeRef.current,
      });

      if (incomingUpdateTime && incomingUpdateTime !== lastUpdateTimeRef.current) {
        lastUpdateTimeRef.current = incomingUpdateTime;
        console.log('[LiveWorkoutModal] SMART MERGE - merging with local changes preserved');

        // Smart merge: Apply remote changes but preserve local unsaved changes
        setExercises(currentExercises => {
          const incomingExercises = liveWorkoutData.planned_exercises;
          const lastSaved = lastSavedExercisesRef.current;

          // If no last saved state, just use incoming
          if (!lastSaved) {
            lastSavedExercisesRef.current = JSON.parse(JSON.stringify(incomingExercises));
            return incomingExercises;
          }

          // Merge exercise by exercise, set by set
          const merged = incomingExercises.map((incomingEx, exIdx) => {
            const currentEx = currentExercises[exIdx];
            const savedEx = lastSaved[exIdx];

            // If exercise doesn't exist locally, use incoming
            if (!currentEx) return incomingEx;

            // Merge sets
            const mergedSets = incomingEx.sets.map((incomingSet, setIdx) => {
              const currentSet = currentEx.sets?.[setIdx];
              const savedSet = savedEx?.sets?.[setIdx];

              // If set doesn't exist locally, use incoming
              if (!currentSet) return incomingSet;

              // For each field, check if local value differs from last saved
              // If it does, keep local (user changed it); otherwise use incoming
              const mergedSet = { ...incomingSet };

              // Fields to check for local changes
              const fieldsToCheck = ['actual_reps', 'actual_weight', 'coach_reps', 'coach_weight'];
              fieldsToCheck.forEach(field => {
                const localValue = currentSet[field];
                const savedValue = savedSet?.[field];
                // If local differs from saved, user changed it locally - keep it
                if (localValue !== savedValue) {
                  mergedSet[field] = localValue;
                }
              });

              return mergedSet;
            });

            return { ...incomingEx, sets: mergedSets };
          });

          // Update last saved to incoming (remote is now our baseline)
          lastSavedExercisesRef.current = JSON.parse(JSON.stringify(incomingExercises));
          return merged;
        });

        // Don't update companion mode from DB during smart merge
        // This preserves the user's local choice
      }
    }
  }, [liveWorkoutData, exercises.length]);

  // Reset all state when modal closes
  useEffect(() => {
    if (!isLiveWorkoutModalOpen) {
      setExercises([]);
      setExpandedExercise(0);
      setIsCompanionMode(false);
      setTotalTimer(0);
      setIsWorkoutStarted(false);
      setStudentActiveTimer(null);
      setStudentSetTimer(0);
      setCoachActiveTimer(null);
      setCoachSetTimer(0);
      setIsSaving(false);
      prevWorkoutIdRef.current = null;
      lastUpdateTimeRef.current = null;
      companionModeInitializedRef.current = null;
      if (totalTimerRef.current) clearInterval(totalTimerRef.current);
      if (studentTimerRef.current) clearInterval(studentTimerRef.current);
      if (coachTimerRef.current) clearInterval(coachTimerRef.current);
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    }
  }, [isLiveWorkoutModalOpen]);

  // Total workout timer
  useEffect(() => {
    if (isWorkoutStarted && isLiveWorkoutModalOpen) {
      totalTimerRef.current = setInterval(() => {
        setTotalTimer(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (totalTimerRef.current) clearInterval(totalTimerRef.current);
    };
  }, [isWorkoutStarted, isLiveWorkoutModalOpen]);

  // Student set timer
  useEffect(() => {
    if (studentActiveTimer) {
      studentTimerRef.current = setInterval(() => {
        setStudentSetTimer(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (studentTimerRef.current) clearInterval(studentTimerRef.current);
    };
  }, [studentActiveTimer]);

  // Coach set timer
  useEffect(() => {
    if (coachActiveTimer) {
      coachTimerRef.current = setInterval(() => {
        setCoachSetTimer(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (coachTimerRef.current) clearInterval(coachTimerRef.current);
    };
  }, [coachActiveTimer]);

  // ESC key to close
  useEffect(() => {
    if (!isLiveWorkoutModalOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isLiveWorkoutModalOpen]);

  // Handlers
  const handleClose = useCallback(() => {
    if (isWorkoutStarted && exercises.some(e => e.sets.some(s => s.actual_reps))) {
      if (!confirm('Kaydedilmemiş veriler kaybolacak. Çıkmak istediğinize emin misiniz?')) {
        return;
      }
    }
    closeLiveWorkoutModal();
  }, [closeLiveWorkoutModal, isWorkoutStarted, exercises]);

  const handleStartWorkout = async () => {
    try {
      await startLiveWorkout(liveWorkoutData.id, isCompanionMode);
      setIsWorkoutStarted(true);
    } catch (error) {
      console.error('Error starting workout:', error);
      toast.error('Antrenman başlatılamadı');
    }
  };

  const handleToggleExercise = (index) => {
    setExpandedExercise(expandedExercise === index ? -1 : index);
  };

  // Debounce timer ref for auto-save
  const autoSaveTimerRef = useRef(null);

  // Debounced auto-save function - saves even before workout is started
  const debouncedAutoSave = useCallback((newExercises) => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(async () => {
      if (liveWorkoutData?.id) {
        console.log('[LiveWorkoutModal] Auto-saving exercises to DB');
        await updateLiveWorkoutExercises(liveWorkoutData.id, newExercises);
        // Update last saved state after successful save
        lastSavedExercisesRef.current = JSON.parse(JSON.stringify(newExercises));
        console.log('[LiveWorkoutModal] Updated lastSavedExercisesRef after save');
      }
    }, 1500); // 1.5 second debounce
  }, [liveWorkoutData?.id, updateLiveWorkoutExercises]);

  const handleUpdateSet = useCallback((exerciseIndex, setIndex, field, value) => {
    setExercises(prev => {
      const newExercises = [...prev];
      const newSets = [...newExercises[exerciseIndex].sets];
      newSets[setIndex] = {
        ...newSets[setIndex],
        [field]: value === '' ? null : value
      };
      newExercises[exerciseIndex] = { ...newExercises[exerciseIndex], sets: newSets };
      // Debounced auto-save for rep/kg changes
      debouncedAutoSave(newExercises);
      return newExercises;
    });
  }, [debouncedAutoSave]);

  const handleUpdateNote = useCallback((exerciseIndex, note) => {
    setExercises(prev => {
      const newExercises = [...prev];
      newExercises[exerciseIndex] = { ...newExercises[exerciseIndex], notes: note };
      debouncedAutoSave(newExercises);
      return newExercises;
    });
  }, [debouncedAutoSave]);

  const handleAddSet = useCallback((exerciseIndex) => {
    setExercises(prev => {
      const newExercises = [...prev];
      const exercise = newExercises[exerciseIndex];
      const lastSet = exercise.sets[exercise.sets.length - 1];
      const newSet = {
        set_number: exercise.sets.length + 1,
        target_reps: lastSet?.target_reps || null,
        target_weight: lastSet?.target_weight || null,
        // Student data
        actual_reps: null,
        actual_weight: null,
        started_at: null,
        completed_at: null,
        duration_seconds: null,
        // Coach data (separate tracking)
        coach_reps: null,
        coach_weight: null,
        coach_started_at: null,
        coach_completed_at: null,
        coach_duration_seconds: null
      };
      newExercises[exerciseIndex] = {
        ...exercise,
        sets: [...exercise.sets, newSet]
      };
      return newExercises;
    });
  }, []);

  const handleRemoveSet = useCallback((exerciseIndex) => {
    setExercises(prev => {
      const newExercises = [...prev];
      const exercise = newExercises[exerciseIndex];
      if (exercise.sets.length > 1) {
        newExercises[exerciseIndex] = {
          ...exercise,
          sets: exercise.sets.slice(0, -1)
        };
      }
      return newExercises;
    });
  }, []);

  // Start timer for student - auto-save to DB for realtime sync
  const handleStartStudentTimer = useCallback(async (exerciseIndex, setIndex) => {
    // Auto-start workout if not started yet
    if (!isWorkoutStarted && liveWorkoutData?.id) {
      try {
        await startLiveWorkout(liveWorkoutData.id, isCompanionMode);
        setIsWorkoutStarted(true);
      } catch (error) {
        console.error('Error auto-starting workout:', error);
        toast.error('Antrenman başlatılamadı');
        return;
      }
    }

    if (studentTimerRef.current) clearInterval(studentTimerRef.current);
    setStudentActiveTimer({ exerciseIndex, setIndex });
    setStudentSetTimer(0);

    setExercises(prev => {
      const newExercises = [...prev];
      const newSets = [...newExercises[exerciseIndex].sets];
      newSets[setIndex] = { ...newSets[setIndex], started_at: new Date().toISOString() };
      newExercises[exerciseIndex] = { ...newExercises[exerciseIndex], sets: newSets };
      // Auto-save to DB for realtime sync
      if (liveWorkoutData?.id) {
        updateLiveWorkoutExercises(liveWorkoutData.id, newExercises);
      }
      return newExercises;
    });
  }, [updateLiveWorkoutExercises, liveWorkoutData?.id, isWorkoutStarted, startLiveWorkout, isCompanionMode]);

  // Stop timer for student - auto-save to DB for realtime sync
  const handleStopStudentTimer = useCallback(() => {
    if (!studentActiveTimer) return;
    const { exerciseIndex, setIndex } = studentActiveTimer;

    setExercises(prev => {
      const newExercises = [...prev];
      const newSets = [...newExercises[exerciseIndex].sets];
      newSets[setIndex] = {
        ...newSets[setIndex],
        completed_at: new Date().toISOString(),
        duration_seconds: studentSetTimer
      };
      newExercises[exerciseIndex] = { ...newExercises[exerciseIndex], sets: newSets };
      // Auto-save to DB for realtime sync
      if (liveWorkoutData?.id) {
        updateLiveWorkoutExercises(liveWorkoutData.id, newExercises);
      }
      return newExercises;
    });

    setStudentActiveTimer(null);
    setStudentSetTimer(0);
  }, [studentActiveTimer, studentSetTimer, updateLiveWorkoutExercises, liveWorkoutData?.id]);

  // Start timer for coach - auto-save to DB for realtime sync
  const handleStartCoachTimer = useCallback(async (exerciseIndex, setIndex) => {
    // Auto-start workout if not started yet
    if (!isWorkoutStarted && liveWorkoutData?.id) {
      try {
        await startLiveWorkout(liveWorkoutData.id, isCompanionMode);
        setIsWorkoutStarted(true);
      } catch (error) {
        console.error('Error auto-starting workout:', error);
        toast.error('Antrenman başlatılamadı');
        return;
      }
    }

    if (coachTimerRef.current) clearInterval(coachTimerRef.current);
    setCoachActiveTimer({ exerciseIndex, setIndex });
    setCoachSetTimer(0);

    setExercises(prev => {
      const newExercises = [...prev];
      const newSets = [...newExercises[exerciseIndex].sets];
      newSets[setIndex] = { ...newSets[setIndex], coach_started_at: new Date().toISOString() };
      newExercises[exerciseIndex] = { ...newExercises[exerciseIndex], sets: newSets };
      // Auto-save to DB for realtime sync
      if (liveWorkoutData?.id) {
        updateLiveWorkoutExercises(liveWorkoutData.id, newExercises);
      }
      return newExercises;
    });
  }, [updateLiveWorkoutExercises, liveWorkoutData?.id, isWorkoutStarted, startLiveWorkout, isCompanionMode]);

  // Stop timer for coach - auto-save to DB for realtime sync
  const handleStopCoachTimer = useCallback(() => {
    if (!coachActiveTimer) return;
    const { exerciseIndex, setIndex } = coachActiveTimer;

    setExercises(prev => {
      const newExercises = [...prev];
      const newSets = [...newExercises[exerciseIndex].sets];
      newSets[setIndex] = {
        ...newSets[setIndex],
        coach_completed_at: new Date().toISOString(),
        coach_duration_seconds: coachSetTimer
      };
      newExercises[exerciseIndex] = { ...newExercises[exerciseIndex], sets: newSets };
      // Auto-save to DB for realtime sync
      if (liveWorkoutData?.id) {
        updateLiveWorkoutExercises(liveWorkoutData.id, newExercises);
      }
      return newExercises;
    });

    setCoachActiveTimer(null);
    setCoachSetTimer(0);
  }, [coachActiveTimer, coachSetTimer, updateLiveWorkoutExercises, liveWorkoutData?.id]);

  // Reset student timer for a single set
  const handleResetStudentTimer = useCallback((exerciseIndex, setIndex) => {
    if (studentActiveTimer?.exerciseIndex === exerciseIndex && studentActiveTimer?.setIndex === setIndex) {
      if (studentTimerRef.current) clearInterval(studentTimerRef.current);
      setStudentActiveTimer(null);
      setStudentSetTimer(0);
    }

    setExercises(prev => {
      const newExercises = [...prev];
      const newSets = [...newExercises[exerciseIndex].sets];
      newSets[setIndex] = { ...newSets[setIndex], started_at: null, completed_at: null, duration_seconds: null };
      newExercises[exerciseIndex] = { ...newExercises[exerciseIndex], sets: newSets };
      return newExercises;
    });
    toast.success('Öğrenci süresi sıfırlandı');
  }, [studentActiveTimer]);

  // Reset coach timer for a single set
  const handleResetCoachTimer = useCallback((exerciseIndex, setIndex) => {
    if (coachActiveTimer?.exerciseIndex === exerciseIndex && coachActiveTimer?.setIndex === setIndex) {
      if (coachTimerRef.current) clearInterval(coachTimerRef.current);
      setCoachActiveTimer(null);
      setCoachSetTimer(0);
    }

    setExercises(prev => {
      const newExercises = [...prev];
      const newSets = [...newExercises[exerciseIndex].sets];
      newSets[setIndex] = { ...newSets[setIndex], coach_started_at: null, coach_completed_at: null, coach_duration_seconds: null };
      newExercises[exerciseIndex] = { ...newExercises[exerciseIndex], sets: newSets };
      return newExercises;
    });
    toast.success('Koç süresi sıfırlandı');
  }, [coachActiveTimer]);

  // Reset all timers for an exercise
  const handleResetExerciseTimers = useCallback((exerciseIndex) => {
    if (studentActiveTimer?.exerciseIndex === exerciseIndex) {
      if (studentTimerRef.current) clearInterval(studentTimerRef.current);
      setStudentActiveTimer(null);
      setStudentSetTimer(0);
    }
    if (coachActiveTimer?.exerciseIndex === exerciseIndex) {
      if (coachTimerRef.current) clearInterval(coachTimerRef.current);
      setCoachActiveTimer(null);
      setCoachSetTimer(0);
    }

    setExercises(prev => {
      const newExercises = [...prev];
      const newSets = newExercises[exerciseIndex].sets.map(set => ({
        ...set,
        started_at: null,
        completed_at: null,
        duration_seconds: null,
        coach_started_at: null,
        coach_completed_at: null,
        coach_duration_seconds: null
      }));
      newExercises[exerciseIndex] = { ...newExercises[exerciseIndex], sets: newSets };
      return newExercises;
    });
    toast.success('Egzersiz süreleri sıfırlandı');
  }, [studentActiveTimer, coachActiveTimer]);

  const handleCompleteWorkout = async () => {
    const hasAnyData = exercises.some(e => e.sets.some(s => s.actual_reps));
    if (!hasAnyData) {
      toast.error('En az bir set girmelisiniz');
      return;
    }

    if (!confirm('Antrenmanı tamamlamak istediğinize emin misiniz? Tüm veriler kaydedilecek.')) {
      return;
    }

    try {
      setIsSaving(true);
      // First save current exercises
      await updateLiveWorkoutExercises(liveWorkoutData.id, exercises);
      // Then complete and save to fitness_workouts
      await completeLiveWorkout(liveWorkoutData.id);
      toast.success('Antrenman tamamlandı!');
      closeLiveWorkoutModal();
    } catch (error) {
      console.error('Error completing workout:', error);
      toast.error('Tamamlama başarısız');
    } finally {
      setIsSaving(false);
    }
  };

  // Progress calculation
  const progress = useMemo(() => {
    const totalSets = exercises.reduce((sum, e) => sum + e.sets.length, 0);
    const completedSets = exercises.reduce(
      (sum, e) => sum + e.sets.filter(s => s.actual_reps !== null && s.actual_reps !== '').length,
      0
    );
    const completedExercises = exercises.filter(
      e => e.sets.every(s => s.actual_reps !== null && s.actual_reps !== '')
    ).length;
    return {
      totalSets,
      completedSets,
      totalExercises: exercises.length,
      completedExercises,
      percentage: totalSets > 0 ? (completedSets / totalSets) * 100 : 0
    };
  }, [exercises]);

  if (!isLiveWorkoutModalOpen || !liveWorkoutData) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-gray-900 flex flex-col"
      >
        {/* Header */}
        <div className="flex-shrink-0 z-10 bg-gray-900 border-b border-gray-800 safe-area-top">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              {/* Left: Close & Title */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleClose}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-lg font-bold text-white">Canlı Antrenman</h1>
                  <p className="text-xs text-gray-500">
                    {format(parseISO(liveWorkoutData.scheduled_date), 'd MMMM yyyy', { locale: tr })}
                    {liveWorkoutData.scheduled_time && ` • ${liveWorkoutData.scheduled_time.substring(0, 5)}`}
                  </p>
                </div>
              </div>

              {/* Right: Timer & Client info */}
              <div className="flex items-center gap-3">
                {/* Total timer */}
                <div className="px-3 py-1.5 bg-gray-800 rounded-lg">
                  <TimerDisplay seconds={totalTimer} isRunning={isWorkoutStarted} size="normal" />
                </div>

                {/* Client avatar */}
                {viewingClientInfo && (
                  <div className="flex items-center gap-2">
                    {viewingClientInfo.avatar_url ? (
                      <img
                        src={viewingClientInfo.avatar_url}
                        alt={viewingClientInfo.display_name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-orange-400" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>{progress.completedExercises}/{progress.totalExercises} egzersiz</span>
                <span>{progress.completedSets}/{progress.totalSets} set</span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-orange-500 to-green-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress.percentage}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            {/* Companion mode toggle - visible if this is a client's workout */}
            {!isWorkoutStarted && (
              viewingClientInfo ||
              (isActingAsCoach && liveWorkoutData?.coach_id && liveWorkoutData?.user_id !== liveWorkoutData?.coach_id)
            ) && (
              <button
                onClick={() => setIsCompanionMode(!isCompanionMode)}
                className={`mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl transition-all ${
                  isCompanionMode
                    ? 'bg-violet-500/20 text-violet-400 border border-violet-500/50'
                    : 'bg-gray-800 text-gray-400 border border-gray-700'
                }`}
              >
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {isCompanionMode ? 'Ben de katılıyorum' : 'Sadece öğrenci'}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div
          className="flex-1 min-h-0 overflow-y-auto pb-4 overscroll-contain touch-pan-y"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Not started yet */}
          {!isWorkoutStarted && (
            <div className="p-4">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-400 font-medium">Antrenman henüz başlamadı</p>
                    <p className="text-xs text-blue-400/70 mt-1">
                      "Başlat" butonuna basarak antrenmanı başlatın. Timer çalışmaya başlayacak.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleStartWorkout}
                className="w-full flex items-center justify-center gap-2 py-4 bg-green-600 hover:bg-green-500 text-white font-semibold rounded-xl transition-colors"
              >
                <Play className="w-5 h-5" />
                <span>Antrenmanı Başlat</span>
              </button>
            </div>
          )}

          {/* Exercises list */}
          {exercises.length > 0 ? (
            <div className="p-4 space-y-3">
              {exercises.map((exercise, index) => (
                <ExerciseCard
                  key={exercise.id || index}
                  exercise={exercise}
                  exerciseIndex={index}
                  isExpanded={expandedExercise === index}
                  onToggle={handleToggleExercise}
                  isCompanionMode={isCompanionMode}
                  onUpdateSet={handleUpdateSet}
                  onAddSet={handleAddSet}
                  onRemoveSet={handleRemoveSet}
                  onUpdateNote={handleUpdateNote}
                  // Student timer
                  onStartStudentTimer={handleStartStudentTimer}
                  onStopStudentTimer={handleStopStudentTimer}
                  onResetStudentTimer={handleResetStudentTimer}
                  studentActiveTimer={studentActiveTimer}
                  studentSetTimer={studentSetTimer}
                  // Coach timer
                  onStartCoachTimer={handleStartCoachTimer}
                  onStopCoachTimer={handleStopCoachTimer}
                  onResetCoachTimer={handleResetCoachTimer}
                  coachActiveTimer={coachActiveTimer}
                  coachSetTimer={coachSetTimer}
                  // Exercise timers reset
                  onResetExerciseTimers={handleResetExerciseTimers}
                  // Avatars
                  clientAvatar={viewingClientInfo?.avatar_url || liveWorkoutData?.client_avatar_url}
                  coachAvatar={liveWorkoutData?.coach_avatar_url}
                />
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="p-4 bg-gray-800 rounded-full w-fit mx-auto mb-4">
                <Dumbbell className="w-8 h-8 text-gray-600" />
              </div>
              <p className="text-gray-400">Henüz egzersiz planlanmamış</p>
              <p className="text-gray-500 text-sm mt-1">Plan düzenleyerek egzersiz ekleyin</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {isWorkoutStarted && (
          <div className="flex-shrink-0 p-4 bg-gray-900 border-t border-gray-800 safe-area-bottom">
            <button
              onClick={handleCompleteWorkout}
              disabled={isSaving}
              className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 hover:bg-green-500 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Flag className="w-5 h-5" />
              )}
              <span>Antrenmanı Tamamla</span>
            </button>
          </div>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default LiveWorkoutModal;
